import React, { useEffect, useState, useCallback } from 'react';
import { statementService } from '../services/api';
import '../styles/Statements.css';

const BLOCKCHAINS = [
  { value: '', label: '-- optional / not chain-specific --' },
  { value: 'tron', label: 'TRON' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'solana', label: 'Solana' },
];

function fmtDateTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  } catch { return String(ts); }
}

function fmtAmount(v) {
  if (v == null) return '—';
  const n = Number(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

export default function Statements() {
  useEffect(() => { document.title = 'Statements • Noctis Ledger'; }, []);

  const [uploads, setUploads]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [form, setForm] = useState({
    walletAddress: '', blockchain: '', label: '', file: null,
  });

  const [expandedId, setExpandedId]   = useState(null);
  const [expandedTxs, setExpandedTxs] = useState([]);
  const [loadingTxs, setLoadingTxs]   = useState(false);

  const [xrefAddress, setXrefAddress] = useState('');
  const [xrefResult, setXrefResult]   = useState(null);
  const [xrefLoading, setXrefLoading] = useState(false);
  const [xrefError, setXrefError]     = useState(null);

  const loadUploads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await statementService.list();
      setUploads(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load statements:', e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  const handleUpload = async () => {
    if (!form.file)            { setUploadError('Choose a file to upload (CSV, XLSX, or PDF)'); return; }
    if (!form.walletAddress)   { setUploadError('Enter the wallet address this statement belongs to'); return; }
    setUploadError(null);
    try {
      setUploading(true);
      await statementService.upload(form.file, {
        walletAddress: form.walletAddress,
        blockchain: form.blockchain || null,
        label: form.label || null,
      });
      setForm({ walletAddress: '', blockchain: '', label: '', file: null });
      const fileInput = document.getElementById('statement-file-input');
      if (fileInput) fileInput.value = '';
      await loadUploads();
    } catch (e) {
      setUploadError(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleExpand = async (uploadId) => {
    if (expandedId === uploadId) { setExpandedId(null); setExpandedTxs([]); return; }
    setExpandedId(uploadId);
    setLoadingTxs(true);
    try {
      const res = await statementService.getTransactions(uploadId);
      setExpandedTxs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setExpandedTxs([]);
    } finally {
      setLoadingTxs(false);
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this statement and all its parsed transactions?')) return;
    try {
      await statementService.delete(uploadId);
      if (expandedId === uploadId) { setExpandedId(null); setExpandedTxs([]); }
      await loadUploads();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCrossReference = async () => {
    if (!xrefAddress.trim()) return;
    setXrefError(null);
    setXrefResult(null);
    setXrefLoading(true);
    try {
      const res = await statementService.crossReference(xrefAddress.trim());
      setXrefResult(res.data);
    } catch (e) {
      setXrefError(e.response?.data?.detail || e.message || 'Lookup failed');
    } finally {
      setXrefLoading(false);
    }
  };

  return (
    <div className="statements-page">
      <div className="statements-header">
        <div className="statements-header-left">
          <h1>📄 Statements</h1>
          <p>Upload wallet statements (CSV / XLSX / PDF) — parsed into a separate database, cross-referenced against the main investigation DB on request.</p>
        </div>
        <button className="statements-close-btn" onClick={() => window.close()}>✕ Close</button>
      </div>

      <div className="statements-body">

        {/* ── Upload form ── */}
        <section className="statements-card">
          <h2>Upload a Statement</h2>
          <div className="statements-form-grid">
            <div className="form-group">
              <label>Wallet Address *</label>
              <input type="text" placeholder="The address this statement belongs to"
                value={form.walletAddress}
                onChange={e => setForm({ ...form, walletAddress: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Blockchain <span className="optional">(optional)</span></label>
              <select value={form.blockchain}
                onChange={e => setForm({ ...form, blockchain: e.target.value })}>
                {BLOCKCHAINS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Label <span className="optional">(optional)</span></label>
              <input type="text" placeholder="e.g., Binance export, Bank statement Q1"
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })} />
            </div>
            <div className="form-group">
              <label>File * <span className="optional">(.csv, .xlsx, .pdf)</span></label>
              <input id="statement-file-input" type="file" accept=".csv,.xlsx,.xls,.pdf"
                onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })} />
            </div>
          </div>
          {uploadError && <div className="statements-error">⚠ {uploadError}</div>}
          <button className="statements-btn-primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? '⏳ Uploading & parsing...' : '⬆ Upload & Parse'}
          </button>
          <p className="statements-hint">
            Header row required for CSV/XLSX. PDF parsing only works on text-based (selectable)
            PDFs — scanned/image PDFs aren't OCR'd. Columns are matched heuristically
            (date/amount/description/counterparty); every original row is kept regardless,
            so you can always download the source file back and check it by eye.
          </p>
        </section>

        {/* ── Cross-reference lookup ── */}
        <section className="statements-card">
          <h2>Cross-Reference an Address</h2>
          <p className="statements-hint" style={{ marginTop: 0 }}>
            Checks this Statements database AND the main investigation database (known address
            registry + on-chain transaction count) in one lookup.
          </p>
          <div className="statements-xref-row">
            <input type="text" placeholder="Wallet address to check..."
              value={xrefAddress}
              onChange={e => setXrefAddress(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCrossReference(); }} />
            <button className="statements-btn-secondary" onClick={handleCrossReference} disabled={xrefLoading}>
              {xrefLoading ? '⏳ Checking...' : '🔍 Check'}
            </button>
          </div>
          {xrefError && <div className="statements-error">⚠ {xrefError}</div>}
          {xrefResult && (
            <div className="statements-xref-result">
              <div className="statements-xref-summary">
                {xrefResult.known_address ? (
                  <span className="xref-pill known">
                    🏷️ {xrefResult.known_address.name} ({xrefResult.known_address.address_type})
                  </span>
                ) : (
                  <span className="xref-pill">No known-address match</span>
                )}
                <span className="xref-pill">{xrefResult.onchain_transaction_count} on-chain TXs (main DB)</span>
                <span className="xref-pill">{xrefResult.statement_match_count} statement rows</span>
              </div>
              {xrefResult.statement_transactions.length > 0 && (
                <div className="statements-table-scroll">
                  <table className="statements-table">
                    <thead>
                      <tr><th>Date</th><th>Description</th><th>Counterparty</th><th>Amount</th><th>Dir</th><th>Currency</th></tr>
                    </thead>
                    <tbody>
                      {xrefResult.statement_transactions.map(r => (
                        <tr key={r.id}>
                          <td>{fmtDateTime(r.tx_date)}</td>
                          <td>{r.description || '—'}</td>
                          <td>{r.counterparty || '—'}</td>
                          <td className={`amt-${r.direction}`}>{fmtAmount(r.amount)}</td>
                          <td>{r.direction || '—'}</td>
                          <td>{r.currency || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Upload history ── */}
        <section className="statements-card">
          <h2>Uploaded Statements</h2>
          {loading ? (
            <p>Loading...</p>
          ) : uploads.length === 0 ? (
            <div className="statements-empty">No statements uploaded yet.</div>
          ) : (
            <div className="statements-uploads-list">
              {uploads.map(u => (
                <div key={u.id} className="statements-upload-row">
                  <div className="statements-upload-main" onClick={() => toggleExpand(u.id)}>
                    <div className="statements-upload-title">
                      <strong>{u.original_filename}</strong>
                      <span className={`statements-status status-${u.status}`}>{u.status}</span>
                    </div>
                    <div className="statements-upload-meta">
                      <span>{u.wallet_address}</span>
                      {u.blockchain && <span className="badge">{u.blockchain.toUpperCase()}</span>}
                      {u.label && <span className="statements-label-tag">{u.label}</span>}
                      <span>{u.row_count} rows</span>
                      <span className="amt-credit">+{fmtAmount(u.total_credit)}</span>
                      <span className="amt-debit">-{fmtAmount(u.total_debit)}</span>
                      <span>{fmtDateTime(u.uploaded_at)}</span>
                    </div>
                    {u.status === 'failed' && u.parse_error && (
                      <div className="statements-error">⚠ {u.parse_error}</div>
                    )}
                  </div>
                  <div className="statements-upload-actions">
                    <a href={statementService.downloadUrl(u.id)} target="_blank" rel="noopener noreferrer"
                       className="statements-icon-btn" title="Download original file">⬇</a>
                    <button className="statements-icon-btn danger" title="Delete"
                      onClick={() => handleDelete(u.id)}>🗑</button>
                  </div>

                  {expandedId === u.id && (
                    <div className="statements-tx-detail">
                      {loadingTxs ? (
                        <p>Loading transactions...</p>
                      ) : expandedTxs.length === 0 ? (
                        <div className="statements-empty">No transactions parsed from this file.</div>
                      ) : (
                        <div className="statements-table-scroll">
                          <table className="statements-table">
                            <thead>
                              <tr><th>Date</th><th>Description</th><th>Counterparty</th><th>Amount</th><th>Dir</th><th>Currency</th><th>Balance</th></tr>
                            </thead>
                            <tbody>
                              {expandedTxs.map(r => (
                                <tr key={r.id}>
                                  <td>{fmtDateTime(r.tx_date)}</td>
                                  <td>{r.description || '—'}</td>
                                  <td>{r.counterparty || '—'}</td>
                                  <td className={`amt-${r.direction}`}>{fmtAmount(r.amount)}</td>
                                  <td>{r.direction || '—'}</td>
                                  <td>{r.currency || '—'}</td>
                                  <td>{r.balance != null ? fmtAmount(r.balance) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
